import produce from 'immer';
import moment from 'moment';
import { AnyAction, Reducer } from 'redux';

import { ComposedActions } from '../../helpers/actions';
import { CatizDisplayLayoutModes } from '../../helpers/constants';
import { arrayToObjParams } from '../../helpers/utils';
import { types as catizActions } from '../actions/catiz_touchpanel.actions';

export const CatizInitialState = {
  statusAreaTabs: {},
  activeTab: 'global',
  touchPanelState: {
    basic: 'CNTRL_BUTTON_1',
    special: 'NONE',
    support: 'BASIC_STATE',
    extra: 'SPECIAL_FNCTNS_BUTTON_NMBR_ONE',
    variable: 'ROLE_SELECT',
  },
  touchPanelButtonStatus: {},
  roleStatus: [],
  textPanel: {
    currentUis: 'ROLE SELECT',
    battleShort: 'OFF',
    radarSensor: 'OFF',
  },
  tabStatusLineColor: {},
  tutorialArea: {},
  mainMonitorLayoutMode: CatizDisplayLayoutModes.CMS,
  //TODO: make this enum? Not a fan of this being string.
  touchPanelLayoutMode: 'cms',
  content: {},
  auxiliarVideo: {},
  processedVideo: {},
};

const changeTouchPanelSet = (state, payload) =>
  produce(state, (draftState: any) => {
    const { TouchPanelSetType, TouchPanelSetValue } = payload.actionPayload;
    draftState.touchPanelState[TouchPanelSetType] = TouchPanelSetValue;
  });

const internalChangeTouchPanelSet = (state, payload) =>
  produce(state, (draftState: any) => {
    const { panelSetType, panelValue } = payload;
    draftState.touchPanelState[panelSetType] = panelValue;
  });

const showTab = (state, payload) =>
  produce(state, (draftState: any) => {
    const { window, tabTitle } = payload.actionPayload;
    draftState.statusAreaTabs = {
      ...state.statusAreaTabs,
      [tabTitle]: {
        show: true,
        window: window,
        title: tabTitle,
      },
    };
  });

const activateTab = (state, payload) =>
  produce(state, (draftState: any) => {
    const { tabTitle } = payload;
    draftState.activeTab = tabTitle;
  });

const setItemsToRoleStatusWindow = (state, payload) =>
  produce(state, (draftState: any) => {
    draftState.roleStatus = payload.actionPayload.roleStatus;
  });

const changeTouchPanelButtonStatus = (state, payload) =>
  produce(state, (draftState: any) => {
    const { TouchPanelButtonClickedId, key, value } = payload;
    draftState.touchPanelButtonStatus[TouchPanelButtonClickedId] = {
      ...draftState.touchPanelButtonStatus[TouchPanelButtonClickedId],
      [key]: value,
    };
  });

const updateTouchPanelContent = (state, payload) =>
  produce(state, (draftState: any) => {
    Object.keys(payload).map((key) => {
      draftState.content[key] = payload[key];
    });
  });

const updateTextPanel = (state, payload) =>
  produce(state, (draftState: any) => {
    draftState.textPanel = payload.actionPayload;
  });

const updateTutorialArea = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const action = arrayToObjParams(actionPayload);
    draftState.tutorialArea = {
      ...state.tutorialArea,
      tutorialCode: action.tutorialCode,
      term: action.term,
      def: action.def,
      showTill: moment().add(3, 'seconds').valueOf(),
    };
  });

const updateStatusLineColor = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { buttonId, buttonIndicatorColor } = actionPayload;
    draftState.tabStatusLineColor[buttonId] = buttonIndicatorColor;
  });

const updateAuxiliarVideo = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    draftState.auxiliarVideo = payload.actionPayload;
  });
const updateProcessedVideo = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    draftState.processedVideo = payload.actionPayload;
  });

const updateLayout = (state: any, payload: any) =>
  produce(state, (draftState: any) => {
    const { actionPayload } = payload;
    const { mainMonitorLayout, touchpanelLayout } = actionPayload;
    switch (mainMonitorLayout) {
      case 'cms':
      case 'classic':
        draftState.mainMonitorLayoutMode = CatizDisplayLayoutModes.CMS;
        break;
      case 'gfcs1':
        draftState.mainMonitorLayoutMode = CatizDisplayLayoutModes.GFCS1;
        break;
      case 'gfcs2':
        draftState.mainMonitorLayoutMode = CatizDisplayLayoutModes.GFCS2;
        break;
      default:
        console.error(
          `Unknown Layout mode for Catiz ${mainMonitorLayout}`,
          payload
        );
        alert(`Unknown layout mode for Catiz ${mainMonitorLayout}`);
        draftState.mainMonitorLayoutMode = CatizDisplayLayoutModes.CMS;
        break;
    }
    draftState.touchPanelLayoutMode = touchpanelLayout;
  });

const reducer: Reducer = (state = CatizInitialState, action: AnyAction) => {
  switch (action.type) {
    case ComposedActions.SystemShowCatizTab.id:
      return showTab(state, action.payload);
    case ComposedActions.SystemChangeTouchPanelSet.id:
      return changeTouchPanelSet(state, action.payload);
    case catizActions.CATIZ_INTERNAL_CHANGE_TP_STATE:
      return internalChangeTouchPanelSet(state, action.payload);
    case ComposedActions.SystemSetItemsToRoleStatusWindow.id:
      return setItemsToRoleStatusWindow(state, action.payload);
    case ComposedActions.SystemIlluminateTouchPanelButton.id:
      const { TouchPanelButtonClickedId, illuminate } =
        action.payload.actionPayload;
      const payload = {
        TouchPanelButtonClickedId: TouchPanelButtonClickedId,
        key: 'isLit',
        value: illuminate,
      };
      return changeTouchPanelButtonStatus(state, payload);
    case ComposedActions.SystemBlinkingTouchPanelButton.id: {
      const { TouchPanelButtonClickedId, blinking } =
        action.payload.actionPayload;
      const payload = {
        TouchPanelButtonClickedId: TouchPanelButtonClickedId,
        key: 'isBlinking',
        value: blinking,
      };
      return changeTouchPanelButtonStatus(state, payload);
    }
    case ComposedActions.SystemEnableTouchPanelButton.id:
      return changeTouchPanelButtonStatus(state, {
        TouchPanelButtonClickedId:
          action.payload.actionPayload.TouchPanelButtonClickedId,
        key: 'isDisabled',
        value: false,
      });
    case ComposedActions.SystemDisableTouchPanelButton.id:
      return changeTouchPanelButtonStatus(state, {
        TouchPanelButtonClickedId:
          action.payload.actionPayload.TouchPanelButtonClickedId,
        key: 'isDisabled',
        value: true,
      });
    case ComposedActions.SystemUpdateTouchPanelButtonProperties.id:
      return updateTouchPanelContent(state, action.payload.actionPayload);
    case ComposedActions.SystemUpdateTextPanel.id:
      return updateTextPanel(state, action.payload);
    case ComposedActions.ActivateCatizTab.uiAction:
      return activateTab(state, action.payload);
    case ComposedActions.ShowTutorialInTutorialArea.id:
      return updateTutorialArea(state, action.payload);
    case ComposedActions.SystemUpdateGlobalTabButtonColor.id:
      return updateStatusLineColor(state, action.payload);
    case ComposedActions.SystemSetCatizLayout.id:
      return updateLayout(state, action.payload);
    case ComposedActions.SystemSetGFCSAuxiliarVideo.id:
      return updateAuxiliarVideo(state, action.payload);
    case ComposedActions.SystemSetGFCSProcessedVideo.id:
      return updateProcessedVideo(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
